import {Box, Typography} from '@mui/material';
import {Image} from '@jobseeker/lotus/components/Image';
import {useWrappedTheme} from '@jobseeker/lotus/hooks';
import {handleTracking} from '@jobseeker/common-core/utils';
import React, {useEffect} from 'react';
import {DriveBanner} from '../utils';
import {Carousel} from '@jobseeker/lotus/components';

const s3Url = process.env.NEXT_PUBLIC_STATIC_HIRIST_OLD;

type TalentBridgeBannerProps = {
  driveBannerObj: DriveBanner;
};

export const TalentBridgeBanner = ({
  driveBannerObj,
}: TalentBridgeBannerProps) => {
  const theme = useWrappedTheme();

  useEffect(() => {
    handleTracking('view=1', {
      event_category: 'Offline Landing page',
      event_label: 'TechDriveHomePageView',
    });
  }, []);

  const Bullet = ({
    src,
    alt,
    label,
  }: {
    src: string;
    alt: string;
    label: string;
  }) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        mb: 2.25,
      }}
    >
      <Image
        src={`${s3Url}${src}`}
        alt={alt}
        width={40}
        height={40}
        objectFit="cover"
        quality={50}
        style={{marginRight: '9px'}}
      />
      <Typography
        sx={{
          fontSize: theme.typography.pxToRem(18),
          fontWeight: 600,
          lineHeight: 1.1,
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  const withCarousel =
    driveBannerObj && driveBannerObj.detailPageImageCarousel
      ? driveBannerObj.detailPageImageCarousel.map(item => {
          return (
            <Image
              key={item}
              src={item}
              alt="banner image"
              width={450}
              height={490}
              style={{
                width: '100%',
                height: '100%',
                borderRadius: 10,
              }}
            />
          );
        })
      : [];

  const bannerTitle = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
      }}
    >
      <Image
        src={`${process.env.NEXT_PUBLIC_STATIC_IIMJOBS}/static/images/live.png`}
        alt="live hiring drive"
        width={22}
        height={22}
      />
      <Box component="span" sx={{color: 'primary.main', ml: 0.375, mr: 1}}>
        {driveBannerObj?.title?.split(' ')[0] || 'Live'}{' '}
      </Box>
      <Box sx={{mr: 1}}>{driveBannerObj?.title?.split(' ')[1] || 'Hiring'}</Box>
      <Box sx={{mr: 1}}>{driveBannerObj?.title?.split(' ')[2] || 'Drive'}</Box>
    </Box>
  );

  const bannerSubtitle = driveBannerObj?.text || 'Premium Tech & Data Roles';

  const bulletsArray =
    driveBannerObj?.eligibility && driveBannerObj?.date && driveBannerObj?.venue
      ? [
          driveBannerObj?.eligibility,
          driveBannerObj?.date,
          driveBannerObj?.venue,
        ]
      : ['', '', ''];

  const bulletIcons = ['/eligibilty.webp', '/calendar.webp', '/venue.webp'];

  return (
    <Box sx={{bgcolor: '#f2f5fa'}}>
      <Box
        sx={{
          bgcolor: '#fff',
          p: {md: '49px 100px 102px 92px', xxs: 6.125},
          pl: {xxs: 2.125, sm: 0},
          pr: {xxs: 2.375, sm: 0},
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{mt: 1.25, maxWidth: '100%'}}>
          <Box>
            <Typography
              sx={{
                fontSize: theme.typography.pxToRem(22),
                fontWeight: 300,
                lineHeight: 'normal',
                mb: 0.625,
                width: 'auto',
              }}
            >
              (Invite Only)
            </Typography>
          </Box>
          <Box>
            <Typography
              component="span"
              sx={{
                fontSize: {
                  md: theme.typography.pxToRem(74),
                  xxs: theme.typography.pxToRem(40),
                },
                fontWeight: 900,
                textTransform: 'uppercase',
                lineHeight: 1.1,
              }}
            >
              {bannerTitle}
            </Typography>
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: theme.typography.pxToRem(26),
                fontWeight: 400,
                lineHeight: 'normal',
                mt: 1.875,
              }}
            >
              {bannerSubtitle}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              mt: 4.375,
            }}
          >
            {bulletsArray.map((bullet: string, idx: number) => (
              <Bullet
                key={idx}
                src={bulletIcons[idx] || '/eligibilty.webp'}
                alt="bullet"
                label={bullet}
              />
            ))}
          </Box>
          <Box
            sx={{
              p: '5px 20px 5px 18px',
              borderRadius: 1,
              border: '1px solid #ffe2d0',
              bgcolor: '#ffeee4',
              width: 'fit-content',
              mt: 3.375,
            }}
          >
            <Typography
              sx={{
                fontSize: theme.typography.pxToRem(18),
                fontWeight: 600,
                lineHeight: 'normal',
                color: 'primary.main',
              }}
            >
              Shortlisted candidates will be sent an invite with further details
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            maxWidth: '450px',
            maxHeight: '490px',
            width: '50%',
            height: '100%',
            borderRadius: 10,
            overflow: 'hidden',
            display: {md: 'block', xxs: 'none'},
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              borderRadius: 10,
              overflow: 'hidden',
            }}
          >
            <Carousel
              carouselItems={withCarousel}
              slidesPerView={1}
              fullFrame={true}
              showArrows={false}
              swiperProps={{
                spaceBetween: 20,
                slidesPerView: 1,
                pagination: {clickable: true},
                autoplay: {delay: 3000, disableOnInteraction: false},
                style: {
                  width: '100%',
                  display: 'flex',
                },
              }}
              singular={true}
              showPagination={false}
              hideNavigationOnMobile={true}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
export default TalentBridgeBanner;
